import { useEffect, useMemo, useRef } from 'react';
import { usePageStore } from '@/store/usePageStore';
import { TAnimationType } from '@/types/global';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { usePrevious } from './usePrevious';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger);

type UseAnimationArgs = {
  refContainer?: React.RefObject<HTMLElement>;
  animationType: TAnimationType;
  delay?: number;
  delayMobile?: number;
  initialOpacity?: number;
  threshold?: number; 
  yVh?: number;
  duration?: number;
  /** optional key (e.g., pathname) to re-arm the animation when this changes */
  resetKey?: string | number | boolean;
};

export const useAnimation = ({
  refContainer,
  animationType,
  delay,
  delayMobile,
  initialOpacity = 0,
  threshold,
  yVh = 5,
  duration,
  resetKey,
}: UseAnimationArgs) => {
  const isSmootherReady = usePageStore((s) => s.isSmootherReady);
  const heightWindowUnit = usePageStore((s) => s.heightWindowUnit);
  const isMobileMenuOpen = usePageStore((s) => s.isMobileMenuOpen);
  const prevIsMobileMenuOpen = usePrevious(isMobileMenuOpen);
  const isMobile = useIsMobile();

  // remember if we've already played on this page
  const playedRef = useRef(false);

  // optional: re-arm when resetKey changes (e.g., on route change)
  useEffect(() => {
    playedRef.current = false;
  }, [resetKey]);

  const conditionalDelay = useMemo(() => {
    // if menu just closed, add the tiny delay so the UI settles
    return (prevIsMobileMenuOpen && !isMobileMenuOpen) 
      ? 0.3 
      : delayMobile || delay || 0;
  }, [prevIsMobileMenuOpen, isMobileMenuOpen, delay, delayMobile]);

  /* Firefox SecurityError in x-origin iframes — ignore */  
  const scrollTriggerRefresh = () => {
    try { ScrollTrigger.refresh(); } catch {} 
  };

  useEffect(() => {
    const el = refContainer?.current;
    if (!el) return;
    if (animationType === 'none') return;
    if (!isSmootherReady) return;
    if (playedRef.current) return; // already ran; do nothing

    // Gate arming: desktop can arm immediately; mobile must wait until menu is closed
    const canArm = !isMobile || (isMobile && !isMobileMenuOpen);
    if (!canArm) return;

    // ---- arm once ----
    const fallbackThreshold = 0.1;
    const thresholdValue =
      typeof threshold === 'number' && threshold >= 0 && threshold <= 1
        ? threshold
        : fallbackThreshold;

    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (!entry.isIntersecting) return;

        const startDelay = isMobile ? conditionalDelay : (delay ?? 0);
        const vhOffset = heightWindowUnit * yVh;

        if (animationType === 'fadeInAndUp') {
          gsap.fromTo(
            el,
            { opacity: initialOpacity ?? 0.15, y: vhOffset },
            {
              opacity: 1,
              y: 0,
              duration: duration ?? 2,
              ease: 'power4.out',
              delay: startDelay,
            }
          );
        } else if (animationType === 'fadeIn') {
          gsap.fromTo(
            el,
            { opacity: initialOpacity ?? 0 },
            {
              opacity: 1,
              duration: duration ?? 0.8,
              ease: 'power2.in',
              delay: startDelay,
            }
          );
        }

        playedRef.current = true; // mark as done
        obs.unobserve(el);

        // If you’re mixing with ScrollTrigger elsewhere, a refresh can help after menu close/layout shift
        if (typeof ScrollTrigger?.refresh === 'function') {
          scrollTriggerRefresh();
        }
      },
      { threshold: thresholdValue }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    refContainer,
    animationType,
    conditionalDelay,
    initialOpacity,
    yVh,
    duration,
    threshold,
    isMobile,
    isMobileMenuOpen, // safe to include; playedRef prevents re-running
    isSmootherReady,
    heightWindowUnit,
    delay
  ]);
};